import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Search, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useDragReorder } from "@/lib/useDragReorder";
import Modal from "@/components/admin/Modal";

const SECTIONS = [
    { id: "modelos", title: "Modelos & Configuração", icon: "Settings" },
    { id: "logistica", title: "Logística", icon: "Truck" },
    { id: "equipamentos", title: "Equipamentos adicionais", icon: "Plug" },
];

const EMPTY = { section_id: "modelos", section_title: SECTIONS[0].title, section_icon: SECTIONS[0].icon, question: "", answer: "", order: 0, published: true };

export default function AdminFaq() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [filter, setFilter] = useState("");
    const [sectionFilter, setSectionFilter] = useState("all");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/faq");
            setItems(data || []);
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function openCreate() {
        setEditing(null);
        setForm({ ...EMPTY, order: items.length + 1 });
        setModalOpen(true);
    }
    function openEdit(it) {
        setEditing(it);
        setForm({
            section_id: it.section_id, section_title: it.section_title, section_icon: it.section_icon || "Settings",
            question: it.question, answer: it.answer, order: it.order, published: it.published,
        });
        setModalOpen(true);
    }
    async function onSave(e) {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/admin/faq/${editing.id}`, form);
                toast.success("Atualizado");
            } else {
                await api.post("/admin/faq", form);
                toast.success("Criado");
            }
            setModalOpen(false);
            load();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao guardar"); }
        finally { setSaving(false); }
    }
    async function onDelete(it) {
        if (!window.confirm(`Apagar a pergunta "${it.question.slice(0, 60)}..."?`)) return;
        try {
            await api.delete(`/admin/faq/${it.id}`);
            toast.success("Apagado");
            load();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
    }
    async function togglePublished(it) {
        try {
            await api.put(`/admin/faq/${it.id}`, { published: !it.published });
            load();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
    }

    const filtered = items.filter((it) => {
        const q = filter.trim().toLowerCase();
        const passSection = sectionFilter === "all" || it.section_id === sectionFilter;
        const passQ = !q || it.question.toLowerCase().includes(q) || it.answer.toLowerCase().includes(q);
        return passSection && passQ;
    });

    const dndEnabled = filter.trim() === "" && sectionFilter === "all";
    const { dragProps, draggingIndex, overIndex } = useDragReorder({ items, setItems, collection: "faq", enabled: dndEnabled });

    function onSectionChange(sid) {
        const meta = SECTIONS.find((s) => s.id === sid) || SECTIONS[0];
        setForm((f) => ({ ...f, section_id: meta.id, section_title: meta.title, section_icon: meta.icon }));
    }

    return (
        <div data-testid="admin-faq-page">
            <PageHeader title="FAQ" subtitle="Perguntas frequentes visíveis na página /faq — arraste as linhas para reordenar" onAdd={openCreate} addLabel="Nova pergunta" addTestId="faq-add" />

            <div className="mt-8 flex gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Pesquisar..."
                        className="w-full rounded-md border border-border bg-white pl-10 pr-3 py-2.5 text-sm outline-none focus:border-primary" />
                </div>
                <select value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}
                    className="rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary">
                    <option value="all">Todas as secções</option>
                    {SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
            </div>

            <div className="mt-6 rounded-2xl border border-border bg-white overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-muted-foreground">A carregar...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center text-muted-foreground">Sem resultados.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-muted/60 text-left">
                                <th className="px-3 py-3 w-8" aria-label="Arrastar" />
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide">Secção</th>
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide">Pergunta</th>
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide w-20">Ordem</th>
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide w-28">Estado</th>
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide w-32 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it, idx) => (
                                <tr key={it.id} data-testid={`faq-row-${it.id}`} {...dragProps(idx)}
                                    className={`border-t border-border hover:bg-muted/30 transition ${draggingIndex === idx ? "opacity-40" : ""} ${overIndex === idx && draggingIndex !== null && draggingIndex !== idx ? "border-t-2 border-t-secondary" : ""}`}>
                                    <td className="px-3 py-3 align-top w-8">
                                        {dndEnabled && (
                                            <span data-testid={`faq-drag-${it.id}`} title="Arrastar para reordenar" className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary inline-flex pt-0.5">
                                                <GripVertical className="h-4 w-4" />
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 align-top text-xs font-bold text-primary/70 uppercase">{it.section_title}</td>
                                    <td className="px-5 py-3 align-top">
                                        <div className="font-semibold text-primary">{it.question}</div>
                                        <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{it.answer}</div>
                                    </td>
                                    <td className="px-5 py-3 align-top text-muted-foreground">{it.order}</td>
                                    <td className="px-5 py-3 align-top">
                                        <button onClick={() => togglePublished(it)}
                                            className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-bold ${it.published ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                            {it.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                            {it.published ? "Publicado" : "Oculto"}
                                        </button>
                                    </td>
                                    <td className="px-5 py-3 align-top text-right">
                                        <div className="inline-flex gap-2">
                                            <button onClick={() => openEdit(it)} data-testid={`faq-edit-${it.id}`}
                                                className="rounded-md p-2 text-primary hover:bg-muted" aria-label="Editar"><Pencil className="h-4 w-4" /></button>
                                            <button onClick={() => onDelete(it)} data-testid={`faq-delete-${it.id}`}
                                                className="rounded-md p-2 text-red-600 hover:bg-red-50" aria-label="Apagar"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Editar pergunta" : "Nova pergunta"} size="lg">
                <form onSubmit={onSave} className="space-y-4" data-testid="faq-form">
                    <Field label="Secção">
                        <select value={form.section_id} onChange={(e) => onSectionChange(e.target.value)} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary">
                            {SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                        </select>
                    </Field>
                    <Field label="Pergunta">
                        <input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })}
                            className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                    </Field>
                    <Field label="Resposta">
                        <textarea required rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })}
                            className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary resize-y" />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Ordem">
                            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                                className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                        <Field label="Visibilidade">
                            <label className="flex items-center gap-2 mt-2 text-sm">
                                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                                Publicado
                            </label>
                        </Field>
                    </div>
                    <div className="flex justify-end gap-3 pt-3 border-t border-border">
                        <button type="button" onClick={() => setModalOpen(false)} className="px-5 py-2.5 text-sm font-semibold rounded-md border border-border hover:bg-muted">Cancelar</button>
                        <button type="submit" disabled={saving} data-testid="faq-save"
                            className="px-6 py-2.5 text-sm font-bold rounded-md bg-primary text-primary-foreground hover:-translate-y-0.5 transition disabled:opacity-60">
                            {saving ? "A guardar..." : (editing ? "Atualizar" : "Criar")}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

export function PageHeader({ title, subtitle, onAdd, addLabel = "Adicionar", addTestId, count }) {
    return (
        <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
                <h1 className="font-heading text-xl lg:text-2xl font-extrabold text-primary leading-tight">{title}{count !== undefined && <span className="ml-2 text-muted-foreground text-base font-bold">({count})</span>}</h1>
                {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
            </div>
            {onAdd && (
                <button onClick={onAdd} data-testid={addTestId}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-xs font-bold text-primary-foreground hover:-translate-y-0.5 transition">
                    <Plus className="h-3.5 w-3.5" /> {addLabel}
                </button>
            )}
        </div>
    );
}

export function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
